/**
 * @file Renders the modal that tracks a long-running action's progress.
 */
import React, { useState } from "react";
import { Progress } from "antd";

import Modal from "./Modal";
import type { ModalRef } from "./types";

type Props = {
  percent?: number | null;
  renderFilters?: (() => React.ReactNode) | null;
  stop: () => void;
  reset: () => void;
  title: string;
  modal: ModalRef;
  start: (...parameters: unknown[]) => void;
};

/**
 * Renders a modal with a progress bar, or with an optional filters form
 * before the tracked action has started.
 */
const ProgressModal = ({
  percent = null,
  renderFilters = null,
  stop,
  reset,
  title,
  modal,
  start,
}: Props): JSX.Element => {
  const [ isOpen, setIsOpen ] = useState(false);

  const hasFilters = renderFilters !== null;

  /**
   * Closes the modal and stops and resets the tracked action.
   */
  const onClose = () => {
    setIsOpen(false);
    stop();
    reset();
  };

  /**
   * Closes the modal.
   */
  modal.current.close = () =>
    onClose();

  /**
   * Opens the modal, and starts the tracked action right away when there
   * are no filters to fill in first.
   */
  modal.current.open = (...parameters: unknown[]) => {
    setIsOpen(true);

    if (!hasFilters) {
      start(...parameters);
    }
  };

  const modalProps = {
    destroyOnClose: true,
    title,
    isOpen,
    onClose,
  };

  const hasStarted = percent !== null;
  const shouldRenderFilters = hasFilters && !hasStarted;

  let content: React.ReactNode = <Progress percent={percent} />;

  if (shouldRenderFilters && renderFilters) {
    content = renderFilters();
  }

  return (
    <Modal {...modalProps}>
      {content}
    </Modal>
  );
};

export default ProgressModal;
